import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ApiService } from '../../../services/api.service';
import { Spettacolo, Stagione } from '../../../shared/interface';
import { ConfirmPopup } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';
import { Router } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-spettacoli-backoffice',
  templateUrl: './spettacoli-backoffice.component.html',
  styleUrl: './spettacoli-backoffice.component.css',
})
export class SpettacoliBackofficeComponent implements OnInit, OnDestroy {

  constructor(
    private apiService: ApiService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {}

  @ViewChild('deleteSpettacolo') deleteSpettacolo!: ConfirmPopup;

  spettacoli: Spettacolo[] = [];
  configurazione?: Stagione;

  search = '';
  filterDal: Date | null = null;
  filterAl: Date | null = null;
  soloStagione = true;
  loading = false;

  private sub?: Subscription;

  ngOnInit(): void {
    this.reload();
    this.sub = this.apiService.spettacoliChanged.subscribe((data) => {
      this.spettacoli = this.normalize(data || []);
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  reload() {
    this.loading = true;
    forkJoin({
      stagione: this.apiService.getStagione().pipe(catchError(() => of(undefined))),
      spettacoli: this.apiService.getSpettacoliBackoffice().pipe(catchError(() => of([] as Spettacolo[]))),
    }).subscribe({
      next: ({ stagione, spettacoli }) => {
        this.configurazione = stagione;
        this.spettacoli = this.normalize(spettacoli || []);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.apiService.message('error', 'Errore', err?.error?.message?.message || 'Errore di caricamento');
      }
    });
  }

  private normalize(list: Spettacolo[]): Spettacolo[] {
    return list.map(s => ({
      ...s,
      dal: s.dal ? new Date(s.dal as any) : s.dal,
      al: s.al ? new Date(s.al as any) : s.al,
    }));
  }

  /** Date stagione in config: dd/MM/yyyy (IT) o YYYY-MM-DD; legacy MM/dd solo se giorno > 12. */
  private parseStagioneDay(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return null;
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    const s = String(value).trim();
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
    if (iso) {
      return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    }
    const dmy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
    if (dmy) {
      const a = Number(dmy[1]);
      const b = Number(dmy[2]);
      const y = Number(dmy[3]);
      let day: number;
      let month: number;
      if (a > 12 && b <= 12) { day = a; month = b; }
      else if (b > 12 && a <= 12) { month = a; day = b; }
      else { day = a; month = b; } // IT dd/MM
      return new Date(y, month - 1, day);
    }
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  get spettacoliFiltrati(): Spettacolo[] {
    let list = [...this.spettacoli];
    if (this.soloStagione && this.configurazione) {
      const from = this.parseStagioneDay(this.configurazione.inizio_stagione);
      const to = this.parseStagioneDay(this.configurazione.fine_stagione);
      if (from && to) {
        list = list.filter(s => {
          const al = this.parseStagioneDay(s.al as any);
          return !!al && al >= from && al <= to;
        });
      }
    }
    const q = this.search.trim().toLowerCase();
    if (q) {
      list = list.filter(s =>
        (s.spettacolo || '').toLowerCase().includes(q) ||
        (s.compagnia || '').toLowerCase().includes(q)
      );
    }
    // Come la tabella precedente: dal ≥ filtroDal, al ≤ filtroAl
    if (this.filterDal) {
      const from = this.parseStagioneDay(this.filterDal)!;
      list = list.filter(s => {
        const dal = this.parseStagioneDay(s.dal as any);
        return !!dal && dal >= from;
      });
    }
    if (this.filterAl) {
      const to = this.parseStagioneDay(this.filterAl)!;
      list = list.filter(s => {
        const al = this.parseStagioneDay(s.al as any);
        return !!al && al <= to;
      });
    }
    return list;
  }

  sameDay(dal: Date | string, al: Date | string): boolean {
    const a = this.parseStagioneDay(dal);
    const b = this.parseStagioneDay(al);
    if (!a || !b) return false;
    return a.getTime() === b.getTime();
  }

  openModifica(s: Spettacolo) {
    this.router.navigate(['/backoffice/spettacoli', s.id]);
  }

  onOpenDeleteSpettacolo(event: Event, spettacolo: Spettacolo) {
    event.preventDefault();
    event.stopPropagation();
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Sei sicuro di voler eliminare lo spettacolo?`,
      reject: () => this.confirmationService.close(),
      accept: () => {
        this.apiService.deleteSpettacolo(spettacolo.id).subscribe({
          error: (err) => this.apiService.message('error', 'Errore', err?.error?.message?.message || 'Errore'),
          complete: () => {
            this.confirmationService.close();
            this.apiService.setSpettacoli();
            this.apiService.message('success', 'Eliminazione', 'Lo spettacolo è stato eliminato con successo!');
          }
        });
      }
    });
  }

  onDeleteSpettacolo() {
    this.deleteSpettacolo.accept();
  }

  onAnnullaDeleteSpettacolo() {
    this.deleteSpettacolo.reject();
  }

}
